import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { useChannel } from '@/context/ChannelContext'
import { usePinnedChannelUUID } from '@/onboarding/usePinnedChannelUUID'
import { bookmarkStep } from '@/api/onboarding'
import { track, trackError } from '@/lib/analytics'
import {
  expectedInnLength,
  getRequisites,
  saveRequisites,
  TIN_TYPE_LABELS,
  validateRequisites,
  type Requisites,
  type TinType,
} from '@/api/requisites'

const TIN_TYPES: TinType[] = ['physical', 'individual', 'legal']

export function RequisitesPage() {
  const navigate = useNavigate()
  const { channels } = useChannel()
  const channelUUID = usePinnedChannelUUID()
  const channel = channels.find((c) => c.uuid === channelUUID)

  const [tinType, setTinType] = useState<TinType>('physical')
  const [fullName, setFullName] = useState('')
  const [inn, setInn] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!channelUUID) return
    getRequisites(channelUUID)
      .then((r) => {
        if (!r) return
        setTinType(r.tinType)
        setFullName(r.fullName)
        setInn(r.inn)
      })
      .catch(() => {})
  }, [channelUUID])

  useEffect(() => {
    if (error) setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullName, inn, tinType])

  const innMaxLen = expectedInnLength(tinType)
  const innDigits = inn.replace(/\D/g, '')
  const innValid = innDigits.length === innMaxLen
  const fullNameValid = fullName.trim().length > 0
  const canSubmit = fullNameValid && innValid && !submitting
  const innStatus = useMemo(() => {
    if (!innDigits.length) return null
    if (innValid) {
      return { tone: 'ok' as const, text: 'Формат верный' }
    }
    const diff = innMaxLen - innDigits.length
    if (diff > 0) {
      return {
        tone: 'error' as const,
        text: `Не хватает ${diff} ${pluralDigits(diff)}`,
      }
    }
    return {
      tone: 'error' as const,
      text: `Нужно ${innMaxLen} цифр`,
    }
  }, [innDigits.length, innMaxLen, innValid])

  const previewUrl = useMemo(() => {
    const id = channel?.maxChannelID?.replace(/^-/, '') || '69092437303297'
    return `max.tgryx.ru/${id}`
  }, [channel?.maxChannelID])

  const previewName = fullName.trim() || 'Иванов Иван Иванович'
  const previewInn = innDigits || '0'.repeat(innMaxLen)

  async function handleSubmit() {
    if (!channelUUID) {
      setError('Канал не выбран')
      return
    }
    if (!fullNameValid) {
      setError(tinType === 'legal' ? 'Укажите название организации.' : 'Укажите ФИО владельца.')
      document.getElementById('req-name')?.focus()
      return
    }
    if (!innValid) {
      setError(`Введите ИНН полностью: ${innMaxLen} цифр.`)
      document.getElementById('req-inn')?.focus()
      return
    }
    const data: Requisites = { tinType, fullName: fullName.trim(), inn: innDigits }
    const errMsg = validateRequisites(data)
    if (errMsg) {
      setError(errMsg)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await saveRequisites(channelUUID, data)
      track('requisites_filled', {
        tinType: data.tinType,
        message: `реквизиты заполнены · ${data.tinType}`,
      })
      bookmarkStep('tariff')
      navigate('/onboarding/tariff')
    } catch (e) {
      trackError(e, 'save-requisites')
      setError(e instanceof Error ? e.message : 'Не удалось сохранить')
      setSubmitting(false)
    }
  }

  return (
    <OnboardingShell
      step="requisites"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate('/onboarding/yandex-done')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          {error && <span className="foot-error">{error}</span>}
          <button className="foot-next" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Сохраняем…' : 'Сохранить и дальше'}
            {!submitting && (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
            )}
          </button>
        </>
      }
    >
      <div className="onb-main-narrow">
        <p className="stage-eyebrow">
          <span className="stage-eyebrow-pill">Маркировка</span>
          Без них модерация Я.Директа не пропустит объявления
        </p>
        <h1 className="stage-title">
          Реквизиты <em>в&nbsp;футер лендинга</em>.
        </h1>
        <p className="stage-sub">
          По закону «О&nbsp;рекламе» в&nbsp;футере лендинга нужно указать, кто его&nbsp;опубликовал. Эти&nbsp;же&nbsp;данные мы передаём в&nbsp;Я.Директ при&nbsp;модерации объявлений&nbsp;— чтобы креативы запустились без&nbsp;ручной проверки. <b>Без&nbsp;ИНН Директ не&nbsp;пропустит объявления на&nbsp;модерацию.</b>
        </p>

        <div className="req-form">
          <div className="req-form-card">
            <div className="req-form-row">
              <label className="req-form-label">Тип</label>
              <div className="req-form-types" role="radiogroup">
                {TIN_TYPES.map((t) => {
                  const meta = TIN_TYPE_LABELS[t]
                  const isOn = tinType === t
                  return (
                    <label key={t} className={`req-type-opt ${isOn ? 'is-on' : ''}`}>
                      <input
                        type="radio"
                        name="req-type"
                        checked={isOn}
                        onChange={() => setTinType(t)}
                      />
                      <span className="req-type-name">{meta.name}</span>
                      <span className="req-type-hint">{meta.hint}</span>
                    </label>
                  )
                })}
              </div>
            </div>

            <div className="req-form-row">
              <label className="req-form-label" htmlFor="req-name">
                {tinType === 'legal' ? 'Название организации' : 'ФИО владельца'}
              </label>
              <input
                id="req-name"
                className="req-form-input"
                type="text"
                placeholder={tinType === 'legal' ? 'ООО «Кофе и Ко»' : 'Иванов Иван Иванович'}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="off"
              />
              <div className="req-form-hint">
                Так&nbsp;же, как в&nbsp;паспорте&nbsp;— Я.Директ сверяет с&nbsp;данными&nbsp;ЕГРЮЛ/ЕГРИП
              </div>
            </div>

            <div className="req-form-row">
              <label className="req-form-label" htmlFor="req-inn">ИНН</label>
              <div className="req-form-inn-row">
                <input
                  id="req-inn"
                  className={`req-form-input req-form-input--inn ${innStatus?.tone === 'error' ? 'is-invalid' : innStatus?.tone === 'ok' ? 'is-valid' : ''}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={innMaxLen}
                  placeholder={'1'.repeat(innMaxLen)}
                  value={inn}
                  onChange={(e) => setInn(e.target.value.replace(/\D/g, '').slice(0, innMaxLen))}
                  autoComplete="off"
                />
                {innStatus && (
                  <div className={`req-form-inn-status ${innStatus.tone}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      {innStatus.tone === 'ok' ? (
                        <path d="M20 6L9 17l-5-5" />
                      ) : (
                        <>
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 8v5" />
                          <circle cx="12" cy="16.5" r=".7" fill="currentColor" stroke="none" />
                        </>
                      )}
                    </svg>
                    {innStatus.text}
                  </div>
                )}
              </div>
              <div className="req-form-hint">
                {innMaxLen === 12 ? '12 цифр для физлица/ИП' : '10 цифр для организации'}
              </div>
            </div>
          </div>

          <div className="req-preview-wrap">
            <div className="req-preview-eye">Превью футера лендинга</div>
            <div className="req-preview-card">
              <div className="req-preview-url">{previewUrl}</div>
              <div className="req-preview-content">
                <div className="req-preview-skel skel-1" />
                <div className="req-preview-skel skel-2" />
                <div className="req-preview-skel skel-3" />
                <div className="req-preview-cta">Подписаться</div>
              </div>
              <div className="req-preview-footer">
                <div className="req-preview-foot-line">© {new Date().getFullYear()} · {previewName}</div>
                <div className="req-preview-foot-line">ИНН&nbsp;{previewInn}</div>
                <div className="req-preview-foot-line req-preview-foot-meta">
                  Реклама. Информация о&nbsp;рекламодателе по&nbsp;ссылке выше
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="req-form-note">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v5" />
            <circle cx="12" cy="16.5" r=".6" fill="currentColor" />
          </svg>
          <span>
            Эти данные можно <b>поменять позже</b> в&nbsp;настройках канала. Они&nbsp;не показываются никому, кроме как в&nbsp;футере вашего лендинга&nbsp;— это требование&nbsp;ОРД.
          </span>
        </div>
      </div>

      <style>{`
        .onb-main-narrow { max-width: 980px; margin: 0 auto; }
        .req-form {
          display: grid;
          grid-template-columns: 1fr 360px;
          gap: 32px;
          align-items: start;
          margin-top: 12px;
        }
        @media (max-width: 900px) {
          .req-form { grid-template-columns: 1fr; }
        }
        .req-form-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 14px;
          padding: 22px 24px;
          display: flex;
          flex-direction: column;
          gap: 22px;
        }
        .req-form-row { display: flex; flex-direction: column; gap: 8px; }
        .req-form-label {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
        }
        .req-form-types {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        @media (max-width: 600px) {
          .req-form-types { grid-template-columns: 1fr; }
        }
        .req-type-opt {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding: 12px 14px;
          border: 1.5px solid var(--tg-line-2, #f0f0f4);
          border-radius: 10px;
          cursor: pointer;
          transition: border-color 150ms, background 150ms;
          background: var(--tg-surface, #fff);
        }
        .req-type-opt:hover { border-color: var(--tg-line-3, #d0d0d8); }
        .req-type-opt.is-on {
          border-color: var(--tg-blue, #0080ff);
          background: var(--tg-blue-bg, #ebf2ff);
        }
        .req-type-opt input { position: absolute; opacity: 0; pointer-events: none; }
        .req-type-name {
          font-size: 14px;
          font-weight: 700;
          color: var(--tg-ink, #111);
        }
        .req-type-hint {
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
        }
        .req-form-input {
          width: 100%;
          padding: 11px 14px;
          font-family: inherit;
          font-size: 15px;
          color: var(--tg-ink, #111);
          background: var(--tg-surface, #fff);
          border: 1.5px solid var(--tg-line-2, #f0f0f4);
          border-radius: 10px;
          outline: none;
          transition: border-color 150ms;
        }
        .req-form-input:focus { border-color: var(--tg-blue, #0080ff); }
        .req-form-input.is-valid {
          border-color: rgba(0, 168, 96, 0.42);
          background: rgba(0, 168, 96, 0.03);
        }
        .req-form-input.is-invalid {
          border-color: rgba(220, 53, 69, 0.45);
          background: rgba(220, 53, 69, 0.04);
        }
        .req-form-input--inn {
          flex: 1;
          font-variant-numeric: tabular-nums;
          letter-spacing: 0.02em;
        }
        .req-form-inn-row { display: flex; gap: 10px; align-items: center; }
        .req-form-inn-status {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 11px;
          background: rgba(0, 168, 96, 0.1);
          color: var(--tg-up, #00a860);
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
        }
        .req-form-inn-status svg { width: 14px; height: 14px; }
        .req-form-inn-status.error {
          background: rgba(220, 53, 69, 0.10);
          color: #dc3545;
        }
        .req-form-hint {
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
          line-height: 1.4;
        }
        .req-preview-wrap { display: flex; flex-direction: column; gap: 8px; }
        .req-preview-eye {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
          padding-left: 4px;
        }
        .req-preview-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 12px 32px -16px rgba(38,40,45,0.16);
        }
        .req-preview-url {
          padding: 8px 14px;
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-mute, #8f9eb0);
          font-size: 11px;
          font-variant-numeric: tabular-nums;
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .req-preview-content {
          padding: 24px 22px 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          align-items: center;
        }
        .req-preview-skel {
          height: 9px;
          border-radius: 6px;
          background: var(--tg-line-2, #f0f0f4);
        }
        .req-preview-skel.skel-1 { width: 70%; }
        .req-preview-skel.skel-2 { width: 90%; }
        .req-preview-skel.skel-3 { width: 80%; }
        .req-preview-cta {
          margin-top: 12px;
          padding: 9px 20px;
          background: var(--tg-blue, #0080ff);
          color: white;
          font-size: 13px;
          font-weight: 700;
          border-radius: 8px;
        }
        .req-preview-footer {
          padding: 14px 22px 18px;
          border-top: 1px solid var(--tg-line-2, #f0f0f4);
          display: flex;
          flex-direction: column;
          gap: 4px;
          background: var(--tg-surface-2, #f9fafb);
        }
        .req-preview-foot-line {
          font-size: 11px;
          color: var(--tg-ink-2, #333);
          font-variant-numeric: tabular-nums;
        }
        .req-preview-foot-meta {
          color: var(--tg-mute, #8f9eb0);
          font-style: italic;
          margin-top: 4px;
        }
        .req-form-note {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-top: 24px;
          padding: 14px 18px;
          background: rgba(255, 213, 80, 0.08);
          border: 1px solid rgba(255, 213, 80, 0.4);
          border-radius: 10px;
          font-size: 13px;
          line-height: 1.5;
          color: var(--tg-ink-2, #333);
        }
        .req-form-note svg { width: 18px; height: 18px; color: rgb(180, 130, 0); flex-shrink: 0; margin-top: 1px; }
        .req-form-note b { color: var(--tg-ink, #111); font-weight: 700; }
        .foot-error {
          font-size: 13px;
          color: #ff4000;
          font-weight: 600;
          margin-right: 12px;
        }
      `}</style>
    </OnboardingShell>
  )
}

function pluralDigits(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'цифра'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'цифры'
  return 'цифр'
}
